import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Layers, ArrowRight } from 'lucide-react';
import { AppShell } from '../components/AppShell';
import { PageContainer } from '../components/ui/PageContainer';
import { PageHeader } from '../components/ui/PageHeader';
import { SegmentedTabs } from '../components/ui/SegmentedTabs';
import { Chip } from '../components/ui/Chip';
import { platformAPI } from '../services/api';
import { useAccess } from '../hooks/useAccess';
import { ServiceIcon } from '../components/services/ServiceIcon';
import {
  SERVICE_CATEGORIES as CATEGORY_META,
  SERVICE_CATEGORY_ORDER as CATEGORY_ORDER,
  categoryOf,
} from '../config/serviceCategories';

// Which services a user sees is decided by the Hub (Business Case x persona,
// see GET /services/registry), not by role lists in the browser.
const NO_ASSIGNMENT_MESSAGE =
  'No services are assigned to your account yet. Ask an administrator to assign you a role in a Business Case.';

const emptyStateMessages = (diagnostics) => {
  const messages = [NO_ASSIGNMENT_MESSAGE];
  const unpaired = diagnostics?.personasWithoutBusinessCase || [];
  const idleBusinessCases = diagnostics?.businessCasesWithoutPersona || [];
  const unknownAssignments = diagnostics?.unknownAssignments || [];
  if (unpaired.length > 0) {
    messages.push(`Your role (${unpaired.map((persona) => persona.label).join(', ')}) is not assigned in any Business Case.`);
  }
  if (idleBusinessCases.length > 0) {
    messages.push(`You belong to ${idleBusinessCases.join(', ')}, but have no role assigned there.`);
  }
  if (unknownAssignments.length > 0) {
    messages.push(`These assignments are not part of the access model and grant nothing: ${unknownAssignments.join(', ')}.`);
  }
  return messages;
};

/* ── Status derivation ───────────────────────────────────────── */
const deriveStatus = (service) => {
  const v = String(service.version || '').toLowerCase();
  if (v.includes('beta'))  return 'beta';
  if (v.includes('alpha')) return 'alpha';
  if (v.startsWith('v') || v.match(/^\d/)) return 'active';
  return 'available';
};

const STATUS_CHIP = {
  active:    'active',
  beta:      'warning',
  alpha:     'critical',
  available: 'neutral',
};
const STATUS_LABEL = {
  active:    'Active',
  beta:      'Beta',
  alpha:     'Alpha',
  available: 'Available',
};

const ServiceTile = ({ service, categoryTitle, isHighlighted }) => {
  const status = deriveStatus(service);
  const ctaLabel = status === 'available' ? 'Enable' : 'Open';

  return (
    <article
      id={`service-${service.id}`}
      className={[
        'flex flex-col gap-3 p-5 bg-white rounded-[12px] border border-[rgba(20,20,20,0.06)] shadow-lb-card transition-all duration-150 hover:shadow-lb-card-hover',
        isHighlighted ? 'ring-2 ring-[#6fb96a]/40' : '',
      ].join(' ')}
      aria-label={service.name}
    >
      <div className="flex items-start justify-between gap-3">
        <ServiceIcon categoryKey={service.categoryKey} size="md" />
        <Chip variant={STATUS_CHIP[status] || 'neutral'}>{STATUS_LABEL[status]}</Chip>
      </div>

      <div className="flex flex-col gap-1">
        {service.code && (
          <span className="text-[11px] font-medium text-lb-muted-soft uppercase tracking-[0.04em]">
            {service.code}
          </span>
        )}
        <h3 className="text-[16px] font-semibold text-lb-ink-strong leading-[1.3] m-0">
          {service.name}
        </h3>
        <p className="text-[14px] text-lb-muted leading-[1.5] m-0 mt-1 line-clamp-3">
          {service.description}
        </p>
      </div>

      <div className="flex items-center justify-between mt-auto pt-3 border-t" style={{ borderColor: 'rgba(20,20,20,0.06)' }}>
        <span className="text-[12px] text-lb-muted leading-none">
          {categoryTitle}
          {service.version && (
            <span className="text-[11px] text-lb-muted-soft ml-1.5">{service.version}</span>
          )}
        </span>
        <button
          type="button"
          className="inline-flex items-center gap-1 min-h-6 p-0 bg-transparent border-0 text-[12.5px] font-medium text-lb-green-primary hover:underline focus-ring rounded"
        >
          {ctaLabel}
          <ArrowRight size={13} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
};

/* ── Page ────────────────────────────────────────────────────── */
export const Services = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { activePersona, bypass, diagnostics, loading: accessLoading } = useAccess();
  const activeService = location.state?.activeService || null;
  // ?service=<id> (from a Home service card) highlights and scrolls to one service.
  const focusServiceId = searchParams.get('service');
  const [services, setServices] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    platformAPI
      .listWp3Services()
      .then((data) => {
        if (!cancelled) {
          setServices(data.services || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Unable to load services');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // The catalogue depends on the persona the user is acting as.
  }, [activePersona]);

  /* If we landed with state.activeService, switch to that tab */
  useEffect(() => {
    if (activeService && CATEGORY_META[activeService]) {
      setActiveCategory(activeService);
    }
  }, [activeService]);

  /* A focused service must be visible whatever tab was chosen */
  useEffect(() => {
    if (focusServiceId) setActiveCategory('all');
  }, [focusServiceId]);

  /* Counts per category tab */
  const counts = useMemo(() => {
    const map = { all: services.length };
    CATEGORY_ORDER.forEach((key) => {
      map[key] = services.filter((s) => s.categoryKey === key).length;
    });
    return map;
  }, [services]);

  /* Visible categories — preserve API ordering, hide empty ones */
  const visibleCategories = useMemo(
    () =>
      CATEGORY_ORDER.filter((key) => (counts[key] || 0) > 0).map((key) => ({
        key,
        ...CATEGORY_META[key],
        count: counts[key],
      })),
    [counts]
  );

  /* Filtered service list */
  const filtered = useMemo(() => {
    if (activeCategory === 'all') return services;
    return services.filter((s) => s.categoryKey === activeCategory);
  }, [services, activeCategory]);

  /* Nothing assigned at all (as opposed to an empty category tab) */
  const noAssignment = !accessLoading && !bypass && !loading && !error && services.length === 0;

  /* Scroll to the focused service, or the highlighted category, after render */
  useEffect(() => {
    if (!focusServiceId && !activeService) return undefined;
    const timer = setTimeout(() => {
      const el = focusServiceId
        ? document.getElementById(`service-${focusServiceId}`)
        : document.querySelector(`[id^="service-"][id$="-${activeService}"], #service-${activeService}`);
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(timer);
  }, [focusServiceId, activeService, filtered.length]);

  return (
    <AppShell title="Services">
      <PageContainer>
        {/* ── Header ───────────────────────────────────────────── */}
        <PageHeader
          title="Services"
          subtitle="Assessments, operations, and reporting modules for your portfolio."
        />

        {/* ── Category tabs ───────────────────────────────────── */}
        <SegmentedTabs
          ariaLabel="Service categories"
          active={activeCategory}
          onChange={setActiveCategory}
          tabs={[
            { id: 'all', label: 'All', count: counts.all || 0 },
            ...visibleCategories.map((cat) => ({ id: cat.key, label: cat.title, count: cat.count })),
          ]}
        />

        {/* ── Error / loading / empty ──────────────────────────── */}
        {error && (
          <div
            className="card-base p-4 text-[13px] font-semibold"
            style={{ background: 'rgba(184,80,82,0.08)', color: '#B85052' }}
          >
            {error}
          </div>
        )}

        {!error && loading && (
          <div className="card-base p-10 text-center text-[12px] text-lb-muted">
            Loading service catalogue…
          </div>
        )}

        {!error && !loading && filtered.length === 0 && (
          <div className="card-base p-10 flex flex-col items-center text-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.04)' }}
              aria-hidden="true"
            >
              <Layers size={20} strokeWidth={1.6} className="text-lb-ghost" />
            </div>
            <h3 className="text-[15px] font-bold text-lb-ink">No Services Available</h3>
            {noAssignment ? (
              emptyStateMessages(diagnostics).map((message) => (
                <p key={message} className="text-[13px] text-lb-muted max-w-md m-0">
                  {message}
                </p>
              ))
            ) : (
              <p className="text-[13px] text-lb-muted max-w-md">
                No services match the selected category yet.
              </p>
            )}
          </div>
        )}

        {/* ── Service grid ─────────────────────────────────────── */}
        {!error && !loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((service) => (
              <ServiceTile
                key={service.id}
                service={service}
                categoryTitle={categoryOf(service.categoryKey).title}
                isHighlighted={activeService === service.categoryKey || focusServiceId === service.id}
              />
            ))}
          </div>
        )}
      </PageContainer>
    </AppShell>
  );
};
